
import { FacebookApi } from '@/api';
import { FB_LOGIN_SCOPE, GENERIC_ERROR_DESCRIPTION } from '@/constants';
import { AppRoute } from '@/router/routes';
import { SelectOption } from '@/types';
import { copyToClipboard } from '@/utilities';
import {
  FacebookMessengerPlatformData,
  FacebookPage,
  SubscribeFacebookPagePayload,
} from 'ignite360-core';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

interface FacebookLoginStatusResponse {
  status: 'connected' | 'not_authorized' | 'unknown';
  authResponse?: FacebookAuthResponse;
}

interface FacebookLoginOptions {
  auth_type?: 'rerequest' | 'reauthenticate' | 'reauthorize';
  scope: string;
  return_scopes?: boolean;
  enable_profile_selector?: boolean;
  profile_selector_ids?: string;
}

interface FacebookLoginResponse {
  authResponse?: FacebookAuthResponse;
}

interface FacebookAuthResponse {
  accessToken: string;
  expiresIn: number;
  data_access_expiration_time: number;
  graphDomain: string;
  signedRequest: string;
  userID: string;
  grantedScopes?: string;
}

declare global {
  interface Window {
    FB: {
      [key: string]: any;
      init(params: Record<string, any>): void;
      getLoginStatus(cb: (response: FacebookLoginStatusResponse) => void | Promise<void>): void;

      login(
        cb: (response: FacebookLoginResponse) => void | Promise<void>,
        options: FacebookLoginOptions,
      ): void;
      logout(cb: () => void | Promise<void>): void;

      getAuthResponse(): FacebookAuthResponse | null;
    };
  }
}

@Component({
  name: 'facebook-login-card',
})
export default class FacebookLoginCard extends Vue {
  @Prop({ required: true, type: String })
  readonly botId!: string;

  @Prop({ required: true, type: String })
  readonly pageId!: string;

  @Prop({ required: false, type: Boolean, default: false })
  readonly hideCopyButton!: boolean;

  @Prop({ required: false, type: String, default: 'Connect to page' })
  readonly header!: string;

  loadedPages: FacebookPage[] = [];

  selectedPageId = '';

  isLoading = false;
  isSubmitting = false;
  isDisconnecting = false;

  async beforeCreate() {
    window.FB.init({
      appId: this.$tenant.facebookAccountId || '',
      autoLogAppEvents: true,
      xfbml: true,
      version: 'v9.0',
    });
  }

  async created() {
    window.FB.getLoginStatus(async (response) => {
      if (response.status === 'connected' && response.authResponse) {
        await this.loadPages(response.authResponse);
      }
    });
  }

  get canSave(): boolean {
    return !!this.selectedPage;
  }

  get canDisconnect(): boolean {
    return !!this.pageId;
  }

  get hasChanges(): boolean {
    return this.selectedPageId !== this.pageId;
  }

  get selectedPage(): FacebookPage | undefined {
    return this.loadedPages.find((page) => page.id === this.selectedPageId);
  }

  get pageOptions(): SelectOption[] {
    return this.loadedPages.map((page) => ({
      value: page.id,
      label: page.name,
    }));
  }

  onLogin(event: MouseEvent) {
    window.FB.login(
      (response) => {
        if (response.authResponse) {
          (async () => {
            try {
              await this.loadPages(response.authResponse!);
            } catch (error) {
              console.error('Error in async operation:', error);
            }
          })();
        }
      },
      {
        scope: FB_LOGIN_SCOPE.join(','),
        // eslint-disable-next-line
        enable_profile_selector: true,
      },
    );
  }

  async onSubmit() {
    const authResponse = window.FB.getAuthResponse();

    if (!this.selectedPage || !authResponse) return;
    this.isSubmitting = true;
    try {
      const facebookPlatformData = await FacebookApi.connectBotWithFacebookPage(this.botId, {
        pageId: this.selectedPage.id,
        userAccessToken: authResponse.accessToken,
        userId: authResponse.userID,
      });
      this.$emit('connect', facebookPlatformData);
      this.$notify.success('Successfully connected to Facebook');
    } catch (e) {
      this.$notify.error({
        title: 'Connecting to Facebook failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  async onDisconnect() {
    if (!this.canDisconnect) return;
    this.isDisconnecting = true;
    try {
      await FacebookApi.disconnectBotFromFacebookPage(this.botId);
      this.$emit('disconnect');
      this.$notify.success('Successfully disconnected from Facebook');
    } catch (e) {
      this.$notify.error({
        title: 'Disconnecting from Facebook failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isDisconnecting = false;
    }
  }

  copyPublicLoginUrl() {
    const resolvedRoute = this.$router.resolve({
      name: AppRoute.PublicSocialLoginFacebook,
      params: { id: this.botId },
    });
    copyToClipboard(location.origin + resolvedRoute.href);
  }

  async loadPages(authResponse: FacebookAuthResponse) {
    this.isLoading = true;
    try {
      this.loadedPages = await FacebookApi.loadFacebookPages(
        authResponse.userID,
        authResponse.accessToken,
      );
      if (this.loadedPages.length === 0 && this.hasChanges) {
        this.reset();
      }
    } catch (e) {
      this.$notify.error({
        title: 'Loading Facebook-pages failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isLoading = false;
    }
  }

  @Watch('pageId', { immediate: true })
  reset() {
    this.selectedPageId = this.pageId;
  }
}
